/*
 * assets/js/src/reference-bublina.js — bublina s citátem na Referencích,
 * ovládaná dotykem.
 *
 * ── PROČ VŮBEC VZNIKL ───────────────────────────────────────────────────
 * Majitel 19. 9. 2026, doslova: „na mobilu Reference. když kliknu na logo
 * firmy s tečkou - nezobrazí se černá bublina s referencí. to by mělo".
 *
 * Do téhle chvíle byla bublina ČISTĚ CSS: `.lcell:hover .lquote` uvnitř
 * `@media (hover: hover)`, a pod 767 px navíc `display: none`. Nebyla to
 * vada — v CSS u toho stálo, že je to záměr návrhu („na dotyku hover
 * neexistuje, takže se nezobrazuje"). Tohle je tedy ZMĚNA NÁVRHU na pokyn
 * majitele, ne oprava; proto sem patří i ten důvod, ne jen kód.
 *
 * ── DESKTOP SE NEMĚNÍ ANI O PIXEL ───────────────────────────────────────
 * Modul se spouští VÝHRADNĚ tam, kde `(hover: none)`. Na desktopu se
 * nevytvoří ani spouštěč, ani posluchač — hover zůstává přesně, jak byl,
 * a `gate:hover-hygiene` nemá co hlásit.
 *
 * ── JEDEN AKTÉR: `click`. DOTYKOVÝ POSLUCHAČ JE JEN BUDÍK ───────────────
 * Past z runbooku TerraHuntu: iOS Chrome po scrollu první `click` na prvek
 * BEZ dotykového posluchače zahodí — chová se, jako by jen „probouzel"
 * stránku. Prvek proto dotykový posluchač mít musí.
 *
 * Ten posluchač ale schválně NIC NEDĚLÁ. První verze tohohle modulu
 * přepínala bublinu v `pointerdown` A ZÁROVEŇ v `click`, a rozdíl hlídala
 * časovým oknem 500 ms — tedy odhadem. Jeden tap by se tak dal snadno
 * vyhodnotit dvakrát (otevřít a hned zavřít), a to okno by se muselo ladit
 * podle zařízení. Přepisovat to bylo správné: **jeden aktér, žádné okno.**
 *
 * `click` navíc na `<button>` vystřelí i z klávesnice (Enter i mezerník),
 * takže přístupnost vyjde zadarmo a ne jako druhá větev.
 *
 * ── SPOUŠTĚČ JE `button`, NE `div` s `onclick` ──────────────────────────
 * Klávesnice ho tak dostane do pořadí sama, čtečka ho přečte jako ovládací
 * prvek a `aria-expanded` řekne, jestli je bublina otevřená. Bez toho by
 * to byla myš-only vlastnost s přístupností dodělanou zvenčí.
 *
 * ── JEDNA ZTRÁTA, PŘIZNANÁ ──────────────────────────────────────────────
 * U buněk, kde je logo zabalené v odkazu na web klienta, spouštěč ten tap
 * převezme, takže se na mobilu na klientův web neproklikne. Změřeno na
 * `/cs/reference/`: 130 buněk, 51 s tečkou, a z nich má logo jako odkaz
 * ven JEDINÁ (Assessment Systems International) — a zrovna ta v bublině
 * žádný odkaz nemá. Je to vědomě přijatá cena za zadání, ne přehlédnutí;
 * na desktopu odkaz funguje dál. Doplnit adresu do textu citátu je oprava
 * obsahu, ne kódu.
 */

const TRIDA_OTEVRENO = 'lqopen';

/** Jen dotyková zařízení. Na desktopu modul neexistuje. */
function jeDotyk() {
	return !!(window.matchMedia && window.matchMedia('(hover: none)').matches);
}

/**
 * Posune bublinu tak, aby se vešla do okna.
 *
 * Návrh ji umisťuje nad logo a u krajních sloupců zarovnává k hraně
 * (`nth-child(5n+1)` a `5n`). Na 390 px je ale bublina skoro přes celou
 * šířku, takže i „zarovnaná" může vyčnívat. Dopočítává se proto AŽ PO
 * otevření, z naměřeného `getBoundingClientRect()` — pevná pravidla v CSS
 * neví, kde na stránce buňka zrovna je.
 *
 * Stejný princip jako u karty mapy (bod K7): prvek se nesmí oříznout, tak
 * se posune, ne schová.
 */
function srovnejDoOkna(bublina) {
	bublina.style.setProperty('--lq-posun', '0px');
	bublina.style.setProperty('--lq-posun-zpet', '0px');

	const okno = document.documentElement.clientWidth;
	const r = bublina.getBoundingClientRect();
	const rezerva = 8;

	let posun = 0;
	if (r.left < rezerva) {
		posun = rezerva - r.left;
	} else if (r.right > okno - rezerva) {
		posun = okno - rezerva - r.right;
	}

	if (posun) {
		const p = Math.round(posun);
		bublina.style.setProperty('--lq-posun', `${p}px`);
		/*
		 * Šipka se posouvá o TOTÉŽ, ale opačně — bublina se uhne od okraje
		 * a špička zůstane nad logem. Protihodnotu počítá JS, ne `calc()`
		 * v CSS: `calc(-1 * var(…, 0))` by potřebovalo nulu S JEDNOTKOU,
		 * a tu zase lint hlásí jako `length-zero-no-unit`. Dvě proměnné
		 * ten spor obejdou bez výjimky v lintu.
		 */
		bublina.style.setProperty('--lq-posun-zpet', `${-p}px`);
	}
}

export function init() {
	if (!jeDotyk()) return 0;

	const bunky = [...document.querySelectorAll('.lcell.hasq')].filter((b) => b.querySelector('.lquote'));
	if (!bunky.length) return 0;

	/** Právě otevřená buňka. Otevřená smí být vždy jen jedna. */
	let otevrena = null;

	/**
	 * @param {boolean} vratFokus Vrátit fokus na spouštěč? `true` jen tam,
	 *        kde zavření vyvolal uživatel od klávesnice nebo od spouštěče.
	 *        Při klepnutí MIMO se fokus nekrade — uživatel míří jinam.
	 */
	function zavri(vratFokus) {
		if (!otevrena) return;
		otevrena.classList.remove(TRIDA_OTEVRENO);
		const t = otevrena.querySelector('.lqtrig');
		if (t) {
			t.setAttribute('aria-expanded', 'false');
			/*
			 * Bez tohohle zůstane po zavření fokus na prvku, který už nic
			 * neovládá — čtečka i klávesnice se ocitnou „nikde" a další Tab
			 * pokračuje od začátku stránky.
			 */
			if (vratFokus) t.focus();
		}
		otevrena = null;
	}

	function otevri(bunka) {
		if (otevrena === bunka) {
			// Druhé klepnutí na TÝŽ spouštěč: zavřít a nechat na něm fokus.
			zavri(true);
			return;
		}
		zavri(false);
		bunka.classList.add(TRIDA_OTEVRENO);
		const t = bunka.querySelector('.lqtrig');
		if (t) t.setAttribute('aria-expanded', 'true');
		otevrena = bunka;
		srovnejDoOkna(bunka.querySelector('.lquote'));
	}

	let pocet = 0;
	bunky.forEach((bunka, i) => {
		const bublina = bunka.querySelector('.lquote');
		if (!bublina.id) bublina.id = `za-lq-${i + 1}`;

		/*
		 * Spouštěč leží PŘES celou buňku. Logo uvnitř může být `<img>`,
		 * nebo `<a><img></a>` — obalit ho `<button>` by v druhém případě
		 * dalo tlačítko do odkazu, což je neplatné HTML. Překryv je proto
		 * sourozenec, ne rodič.
		 */
		/*
		 * ODKAZ NA LOGU SE NA DOTYKU ROZBALÍ.
		 *
		 * U buněk, kde je logo zabalené v `<a href>` na web klienta, by po
		 * přidání spouštěče zůstal v DOM odkaz, který je VIDĚT a NEJDE na
		 * něj klepnout — překrývá ho `button.lqtrig`. Dotyková brána to
		 * hlásí správně jako vadu („a v lcell hasq — překrývá button")
		 * a má pravdu i věcně: neklikatelný odkaz je horší než žádný,
		 * protože ho čtečka pořád nabízí a prst na něj nedosáhne.
		 *
		 * Adresa se neztrácí — táž hodnota se na dotyku vypisuje jako
		 * `a.lqweb` UVNITŘ bubliny (viz `page-templates/template-reference.php`).
		 * Tohle tedy odkaz nemaže, jen ho přesouvá tam, kam se dá dosáhnout.
		 *
		 * Na desktopu se nic z tohohle nespustí: celý modul běží jen při
		 * `(hover: none)`, takže tam logo zůstává odkazem jako dosud.
		 */
		const odkazNaLogu = bunka.querySelector('a[href]:not(.lqweb)');
		if (odkazNaLogu) {
			const rodic = odkazNaLogu.parentNode;
			while (odkazNaLogu.firstChild) rodic.insertBefore(odkazNaLogu.firstChild, odkazNaLogu);
			rodic.removeChild(odkazNaLogu);
		}

		const trig = document.createElement('button');
		trig.type = 'button';
		trig.className = 'lqtrig';
		trig.setAttribute('aria-expanded', 'false');
		trig.setAttribute('aria-controls', bublina.id);

		// Jméno pro čtečku: bez něj je to „tlačítko" bez obsahu. Bere se
		// z `alt` loga, které tam vždy je (měřeno: 1 610 obrázků, všechny s `alt`).
		const logo = bunka.querySelector('img');
		const jmeno = logo ? (logo.getAttribute('alt') || '').trim() : '';
		trig.setAttribute('aria-label', jmeno ? `Reference: ${jmeno}` : 'Zobrazit referenci');

		bunka.appendChild(trig);

		/*
		 * BUDÍK PRO iOS: posluchač existuje, ale nic nedělá. Jeho jediná
		 * role je, aby prohlížeč prvek považoval za dotykový a nezahodil
		 * na něm první tap po scrollu. `passive: true`, ať nebrzdí rolování.
		 */
		trig.addEventListener('touchstart', () => {}, { passive: true });

		// JEDINÝ AKTÉR. Z klávesnice (Enter i mezerník) vystřelí taky.
		trig.addEventListener('click', (e) => {
			e.preventDefault();
			otevri(bunka);
		});

		pocet += 1;
	});

	// Tap mimo zavře. `pointerdown` na dokumentu, aby to platilo i na iOS
	// napoprvé; spouštěč si událost bere sám a sem se nedostane.
	document.addEventListener('pointerdown', (e) => {
		if (!otevrena) return;
		if (otevrena.contains(e.target)) return;
		zavri(false);
	});

	// Esc zavírá a fokus se vrací na spouštěč — klávesnice nesmí skončit nikde.
	document.addEventListener('keydown', (e) => {
		if ('Escape' === e.key) zavri(true);
	});

	// Po otočení nebo změně šířky přepočítat; jinak by posun držel starou
	// hodnotu a bublina by vyčnívala na druhou stranu.
	window.addEventListener('resize', () => {
		if (otevrena) srovnejDoOkna(otevrena.querySelector('.lquote'));
	});

	return pocet;
}
